import { useTranslation } from 'react-i18next';
import { memo, useCallback, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import { JustInTimeRoutingConfig } from '@thive/fddm-types';
import { MinusIcon, PlusIcon } from '@/assets/icons';
import { TargetInputCanvas as TargetInput } from '@/modules/common/components/inputs/components/TargetInputCanvas';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs';

const MIN_RELATIVE_POSITION = 0;
const MAX_RELATIVE_POSITION = 100;

type AreaItem = { value: string; label: string };

type JitRoutingPointProps = {
  jitRoutingPoint: JustInTimeRoutingConfig | null;
  allAreaItems: AreaItem[];
  waitAreaItems: AreaItem[];
  targetAreaItems: AreaItem[];
  onSelectTarget: (targetId: string, jitRoutingPointId: string, oldValue?: string) => void;
  onRemoveTarget: (targetId: string, jitRoutingPointId: string) => void;
  onRemoveJitRoutingPoint: (jitRoutingPointId: string) => void;
  onSelectWaitArea: (waitAreaId: string, jitRoutingPointId: string, oldValue?: string) => void;
  onRemoveWaitArea: (waitAreaId: string, jitRoutingPointId: string) => void;
  onSetRelativePosition: (value: number, jitRoutingPointId: string) => void;
  onCreate?: (targetId: string) => void;
  onDismissDraft?: () => void;
};

function JitRoutingPointComponent({
  jitRoutingPoint,
  allAreaItems,
  waitAreaItems,
  targetAreaItems,
  onSelectTarget,
  onRemoveTarget,
  onRemoveJitRoutingPoint,
  onSelectWaitArea,
  onRemoveWaitArea,
  onSetRelativePosition,
  onCreate,
  onDismissDraft,
}: JitRoutingPointProps) {
  const { t } = useTranslation('interface');
  const isDraft = onDismissDraft !== undefined;
  const [showAddTarget, setShowAddTarget] = useState(
    () => !jitRoutingPoint || jitRoutingPoint.targetAreaIds.length === 0,
  );
  const [showAddWait, setShowAddWait] = useState(
    () => !!jitRoutingPoint && jitRoutingPoint.waitAreaIds.length === 0,
  );

  const handleSelectExistingTarget = useCallback(
    (targetId: string, _id?: string, oldValue?: string) => {
      if (jitRoutingPoint) onSelectTarget(targetId, jitRoutingPoint.id, oldValue);
    },
    [jitRoutingPoint, onSelectTarget],
  );

  const handleAddTargetSelect = useCallback(
    (targetId: string) => {
      if (jitRoutingPoint) onSelectTarget(targetId, jitRoutingPoint.id);
      else {
        setShowAddWait(true);
        onCreate?.(targetId);
      }
    },
    [jitRoutingPoint, onSelectTarget, onCreate],
  );

  const handleSelectWaitArea = useCallback(
    (waitAreaId: string, _id?: string, oldValue?: string) => {
      if (jitRoutingPoint) onSelectWaitArea(waitAreaId, jitRoutingPoint.id, oldValue);
    },
    [jitRoutingPoint, onSelectWaitArea],
  );

  const handleRemoveTarget = useCallback(
    (targetId: string) => {
      if (jitRoutingPoint) onRemoveTarget(targetId, jitRoutingPoint.id);
    },
    [jitRoutingPoint, onRemoveTarget],
  );

  const handleRemoveWaitArea = useCallback(
    (waitAreaId: string) => {
      if (jitRoutingPoint) onRemoveWaitArea(waitAreaId, jitRoutingPoint.id);
    },
    [jitRoutingPoint, onRemoveWaitArea],
  );

  const handleRemoveTop = useCallback(() => {
    if (jitRoutingPoint) onRemoveJitRoutingPoint(jitRoutingPoint.id);
    else onDismissDraft?.();
  }, [jitRoutingPoint, onRemoveJitRoutingPoint, onDismissDraft]);

  // Cancelling the picker in the draft slot graduates it: the parent drops
  // `showDraft`, the row reappears under its real key in the regular list.
  const handleCancelAddTarget = useCallback(() => {
    if (isDraft) onDismissDraft?.();
    else setShowAddTarget(false);
  }, [isDraft, onDismissDraft]);

  const handleCancelAddWait = useCallback(() => {
    setShowAddWait(false);
  }, []);

  const handleSetRelativePosition = useCallback(
    (value: number) => {
      if (!jitRoutingPoint) return;
      if (value < MIN_RELATIVE_POSITION || value > MAX_RELATIVE_POSITION) return;
      onSetRelativePosition(value, jitRoutingPoint.id);
    },
    [jitRoutingPoint, onSetRelativePosition],
  );

  const targetAreaType = t('layers.delivery', 'Delivery');
  const waitAreaType = t('jit_routing_points.wait_area', 'wait area');

  const title =
    jitRoutingPoint?.name ?? t('jit_routing_points.new_jit_routing_point', 'New JIT routing point');

  return (
    <Stack gap={1} direction='column'>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography fontWeight={600} fontSize={14}>
          {title}
        </Typography>

        <IconButton onClick={handleRemoveTop} size='small' sx={{ p: 0, height: 'fit-content' }}>
          <MinusIcon />
        </IconButton>
      </Stack>

      {jitRoutingPoint?.targetAreaIds.map((targetId, index) => {
        const currentArea = allAreaItems.find((item) => item.value === targetId);
        const dropdownItems = currentArea ? [currentArea, ...targetAreaItems] : targetAreaItems;
        return (
          <PropertiesLabel
            key={`target-${targetId}`}
            skipFocus
            i18nkey='interface:jit_routing_points.target'
            amount={index + 1}
          >
            <TargetInput
              value={targetId}
              onRemove={() => handleRemoveTarget(targetId)}
              disableRemove={jitRoutingPoint.targetAreaIds.length === 1}
              onTargetSelect={handleSelectExistingTarget}
              dropdownItems={dropdownItems}
              areaType={targetAreaType}
            />
          </PropertiesLabel>
        );
      })}

      {showAddTarget && (
        <PropertiesLabel
          key='add-target'
          skipFocus
          i18nkey='interface:jit_routing_points.target'
          amount={(jitRoutingPoint?.targetAreaIds.length ?? 0) + 1}
        >
          <TargetInput
            dropdownItems={targetAreaItems}
            onTargetSelect={handleAddTargetSelect}
            onRemove={handleCancelAddTarget}
            onPickerCancel={handleCancelAddTarget}
            value=''
            disableRemove={!jitRoutingPoint || jitRoutingPoint.targetAreaIds.length === 0}
            areaType={targetAreaType}
            activateOnStart
          />
        </PropertiesLabel>
      )}

      <Stack justifyContent='flex-end' alignItems='center' direction='row'>
        <IconButton
          onClick={() => setShowAddTarget(true)}
          size='small'
          disabled={showAddTarget}
          sx={{ p: 0, height: 'fit-content' }}
        >
          <PlusIcon />
        </IconButton>
      </Stack>

      {jitRoutingPoint && (
        <>
          {jitRoutingPoint.waitAreaIds.map((waitAreaId, index) => {
            const currentArea = allAreaItems.find((item) => item.value === waitAreaId);
            const dropdownItems = currentArea ? [currentArea, ...waitAreaItems] : waitAreaItems;
            return (
              <PropertiesLabel
                key={`wait-${waitAreaId}`}
                skipFocus
                i18nkey='interface:jit_routing_points.wait_area_amount'
                amount={index + 1}
              >
                <TargetInput
                  value={waitAreaId}
                  onTargetSelect={handleSelectWaitArea}
                  onRemove={() => handleRemoveWaitArea(waitAreaId)}
                  dropdownItems={dropdownItems}
                  areaType={waitAreaType}
                />
              </PropertiesLabel>
            );
          })}

          {showAddWait && (
            <PropertiesLabel
              key='add-wait'
              skipFocus
              i18nkey='interface:jit_routing_points.wait_area_amount'
              amount={jitRoutingPoint.waitAreaIds.length + 1}
            >
              <TargetInput
                onTargetSelect={handleSelectWaitArea}
                dropdownItems={waitAreaItems}
                onRemove={handleCancelAddWait}
                onPickerCancel={handleCancelAddWait}
                value=''
                disableRemove={jitRoutingPoint.waitAreaIds.length === 0}
                areaType={waitAreaType}
              />
            </PropertiesLabel>
          )}

          <Stack justifyContent='flex-end' alignItems='center' direction='row'>
            <IconButton
              onClick={() => setShowAddWait(true)}
              size='small'
              disabled={showAddWait}
              sx={{ p: 0, height: 'fit-content' }}
            >
              <PlusIcon />
            </IconButton>
          </Stack>

          <PropertiesLabel i18nkey='interface:jit_routing_points.relative_position'>
            <NumberInput
              value={jitRoutingPoint.relativePositionOnHighwayInPerc}
              onChange={handleSetRelativePosition}
              unit={t(`interface:settings.units.percentage`)}
              minValue={0}
              minValueErrorMsg={t('common:minimum_value', { minValue: MIN_RELATIVE_POSITION })}
              maxValue={100}
              maxValueErrorMsg={t('common:maximum_value', { maxValue: MAX_RELATIVE_POSITION })}
            />
          </PropertiesLabel>
        </>
      )}
    </Stack>
  );
}

export const JitRoutingPoint = memo(JitRoutingPointComponent);
